import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/opt/build/repo/node_modules/gatsby-theme-docz/src/base/Layout.js";
import { Playground } from "docz";
import Counter from "../examples/Counter";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "getting-started"
    }}>{`Getting Started`}</h1>
    <p>{`This section will walk you through the main features of `}<inlineCode parentName="p">{`re-reduced`}</inlineCode>{` with practical examples:`}</p>
    <p>{`In the following examples we'll be building a simple counter app.`}</p>
    <h2 {...{
      "id": "defining-a-type-system"
    }}>{`Defining a type system`}</h2>
    <p><inlineCode parentName="p">{`[root]/examples/Counter/types.ts`}</inlineCode></p>
    <pre><code parentName="pre" {...{
        "className": "language-ts"
      }}>{`export interface State {
  counter: number;
}
`}</code></pre>
    <h2 {...{
      "id": "creating-actions"
    }}>{`Creating Actions`}</h2>
    <p><inlineCode parentName="p">{`[root]/examples/Counter/actions.ts`}</inlineCode></p>
    <pre><code parentName="pre" {...{
        "className": "language-ts"
      }}>{`import { createAction } from "re-reduced";

export default {
  adjust: createAction<number>("ADJUST"),
  decrement: createAction("DECREMENT"),
  increment: createAction("INCREMENT"),
};
`}</code></pre>
    <p>{`alternatively, actions can be created in bulk using `}<inlineCode parentName="p">{`createActions`}</inlineCode></p>
    <blockquote>
      <p parentName="blockquote">{`Note that, when using createActions, the action type is infered from the action name and namespace`}</p>
    </blockquote>
    <pre><code parentName="pre" {...{
        "className": "language-ts"
      }}>{`import { createActions } from "re-reduced";

export default createActions("COUNTER", create => ({
  adjust: create.action<number>(),
  decrement: create.action(),
  increment: create.action(),
}));
`}</code></pre>
    <h2 {...{
      "id": "creating-reducers"
    }}>{`Creating Reducers`}</h2>
    <p><inlineCode parentName="p">{`[root]/examples/Counter/reducers.ts`}</inlineCode></p>
    <pre><code parentName="pre" {...{
        "className": "language-ts"
      }}>{`import { combineReducers } from "redux";
import { createReducer, match } from "re-reduced";

import actions from "./actions";
import { State } from "./types";

const INITIAL_STATE = 0;

export const counter = createReducer<number>(
  [
    actions.increment.reduce(state => state + 1),
    actions.decrement.reduce(state => state - 1),
    actions.adjust.reduce((state, payload) => state + payload),
  ],
  INITIAL_STATE
);

// which can also be written like:

export const counter = createReducer<number>(
  [
    match(actions.increment, state => state + 1),
    match(actions.decrement, state => state - 1),
    match(actions.adjust, (state, payload) => state + payload),
  ],
  INITIAL_STATE
);

export default combineReducers<State>({
  counter,
});
`}</code></pre>
    <h2 {...{
      "id": "creating-selectors"
    }}>{`Creating selectors`}</h2>
    <p><inlineCode parentName="p">{`Selectors`}</inlineCode>{` in redux are functions defined in terms of the application state and perform `}<inlineCode parentName="p">{`derivation`}</inlineCode>{` and `}<inlineCode parentName="p">{`aggregation`}</inlineCode>{`.`}</p>
    <blockquote>
      <p parentName="blockquote"><a parentName="p" {...{
          "href": "https://github.com/reduxjs/reselect"
        }}><inlineCode parentName="a">{`reselect`}</inlineCode></a>{` is the recommended library for selector composition.`}</p>
    </blockquote>
    <pre><code parentName="pre" {...{
        "className": "language-ts"
      }}>{`import { createSelector } from "reselect";

import { State } from "./types";

export const getCounter = (state: State) => state.counter;

export const getCounterIsOdd = createSelector(
  getCounter,
  counter => counter % 2 !== 0
);

export const getCounterIsPositive = createSelector(
  getCounter,
  counter => counter >= 0
);
`}</code></pre>
    <h2 {...{
      "id": "connecting-a-component"
    }}>{`Connecting a component`}</h2>
    <p>{`Re-reduced provides the `}<inlineCode parentName="p">{`connectWithActions`}</inlineCode>{` helper function for easily connecting state and actions to a component.`}</p>
    <p><inlineCode parentName="p">{`[root]/src/ui/containers/Counter.tsx`}</inlineCode></p>
    <pre><code parentName="pre" {...{
        "className": "language-ts"
      }}>{`import * as React from "react";
import { connectWithActions } from "re-reduced";

import actions from "./actions";
import * as selectors from "./selectors";

interface Props {
  count: number;
  isOdd: boolean;
  isPositive: boolean;
  actions: typeof actions;
}

const Counter = (props: Props) => (
  <section>
    <button onClick={() => props.actions.decrement()}>-1</button>
    <button onClick={() => props.actions.adjust(-5)}>-5</button>
    <button
      disabled
      style={{
        color: props.isPositive ? (props.isOdd ? "blue" : "green") : "red",
        fontWeight: "bold",
        paddingLeft: 5,
        paddingRight: 5,
        width: 50,
      }}
    >
      {props.count}
    </button>
    <button onClick={() => props.actions.adjust(5)}>+5</button>
    <button onClick={() => props.actions.increment()}>+1</button>
  </section>
);

const enhance = connectWithActions<Props>(actions, {
  count: selectors.getCounter,
  isOdd: selectors.getCounterIsOdd,
  isPositive: selectors.getCounterIsPositive,
});

export default enhance(Counter);
`}</code></pre>
    <blockquote>
      <p parentName="blockquote">{`Note that `}<inlineCode parentName="p">{`connectWithActions`}</inlineCode>{` does not provide 100% feature parity with `}<a parentName="p" {...{
          "href": "https://github.com/reduxjs/react-redux"
        }}>{`react-redux's`}</a>{` `}<a parentName="p" {...{
          "href": "https://react-redux.js.org/introduction/quick-start#connect"
        }}>{`connect`}</a>{`.`}</p>
    </blockquote>
    <p>{`The above `}<inlineCode parentName="p">{`enhance`}</inlineCode>{` example can be easily rewritten without `}<inlineCode parentName="p">{`connectWithActions`}</inlineCode>{`, using `}<inlineCode parentName="p">{`connect`}</inlineCode>{`, `}<inlineCode parentName="p">{`applySpec`}</inlineCode>{` and `}<inlineCode parentName="p">{`bindActionCreators`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ts"
      }}>{`import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { applySpec } from "ramda";

const enhance = connect(
  applySpec({
    count: selectors.getCounter,
    isOdd: selectors.getCounterIsOdd,
    isPositive: selectors.getCounterIsPositive,
  }),
  dispatch => ({
    actions: bindActionCreators(actions, dispatch),
  })
);
`}</code></pre>
    <p>{`Alternatively, from version `}<inlineCode parentName="p">{`1.5.0`}</inlineCode>{`, re-reduced supports hooks, using the following syntax:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`import { useActions, useReduxState } from "re-reduced";

import actions from "./actions";
import * as selectors from "./selectors";

function Counter() {
  const { decrement, increment, adjust } = useActions(actions);
  const { count, isOdd, isPositive } = useReduxState({
    count: selectors.getCounter,
    isOdd: selectors.getCounterIsOdd,
    isPositive: selectors.getCounterIsPositive,
  });

  return <div>{/* app render logic... */}</div>;
}
`}</code></pre>
    <h2 {...{
      "id": "demo"
    }}>{`Demo`}</h2>


    <Playground __position={0} __code={'<Counter />'} __scope={{
      props,
      DefaultLayout,
      Playground,
      Counter,
      DefaultLayout,
      _frontmatter
    }} mdxType="Playground">
  <Counter mdxType="Counter" />
    </Playground>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      